import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './common.css';

const TagsInput = ({
  label,
  value = [],
  onChange,
  options = [],
  onCreateTag,
  required,
  helperText,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState(options);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Sync with parent value
  useEffect(() => {
    if (Array.isArray(value)) {
      const tags = value.map((tagId) => {
        const tag = availableTags.find((t) => t.value === tagId);
        return tag || { value: tagId, label: `Tag ${tagId}` };
      });
      setSelectedTags(tags);
    }
  }, [value, availableTags]);

  // Update available tags when options change
  useEffect(() => {
    setAvailableTags(options);
  }, [options]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
  };

  const handleInputKeyDown = async (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      await addTag(inputValue.trim());
    } else if (
      e.key === 'Backspace' &&
      !inputValue &&
      selectedTags.length > 0
    ) {
      // Remove last tag on backspace if input is empty
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const addTag = async (tagName) => {
    // Check if tag already selected
    if (
      selectedTags.some(
        (tag) => tag.label.toLowerCase() === tagName.toLowerCase()
      )
    ) {
      toast.warning('Tag already added');
      setInputValue('');
      return;
    }

    // Check if tag exists in available tags
    let existingTag = availableTags.find(
      (tag) => tag.label.toLowerCase() === tagName.toLowerCase()
    );

    if (!existingTag && onCreateTag) {
      // Create new tag
      try {
        const newTagId = await onCreateTag(tagName);
        if (newTagId) {
          existingTag = { value: newTagId, label: tagName };
          setAvailableTags([...availableTags, existingTag]);
        } else {
          return; // Creation failed
        }
      } catch (error) {
        console.error('Error creating tag:', error);
        return;
      }
    }

    if (existingTag) {
      const newSelectedTags = [...selectedTags, existingTag];
      const tagIds = newSelectedTags.map((tag) => tag.value);
      setSelectedTags(newSelectedTags);
      onChange(tagIds);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove) => {
    const newSelectedTags = selectedTags.filter(
      (tag) => tag.value !== tagToRemove.value
    );
    const tagIds = newSelectedTags.map((tag) => tag.value);
    setSelectedTags(newSelectedTags);
    onChange(tagIds);
  };

  const selectSuggestion = (tag) => {
    if (!selectedTags.some((t) => t.value === tag.value)) {
      const newSelectedTags = [...selectedTags, tag];
      const tagIds = newSelectedTags.map((t) => t.value);
      setSelectedTags(newSelectedTags);
      onChange(tagIds);
    }
    setInputValue('');
    setShowSuggestions(false);
  };

  const filteredSuggestions = availableTags.filter(
    (tag) =>
      tag.label.toLowerCase().includes(inputValue.toLowerCase()) &&
      !selectedTags.some((selectedTag) => selectedTag.value === tag.value)
  );

  return (
    <div className="tags-input-container">
      <label className="tags-input-label">
        {label}
        {required && <span className="required-asterisk"> *</span>}
      </label>

      <div className={`tags-input-wrapper ${disabled ? 'disabled' : ''}`}>
        <div className="tags-container">
          {selectedTags.map((tag) => (
            <span key={tag.value} className="tag-chip">
              {tag.label}
              {!disabled && (
                <button
                  type="button"
                  className="tag-remove"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove ${tag.label}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}

          <input
            type="text"
            className="tags-input-field"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={() => setShowSuggestions(inputValue.length > 0)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            placeholder={
              selectedTags.length === 0 ? 'Type and press Enter...' : ''
            }
            disabled={disabled}
          />
        </div>

        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="tags-suggestions">
            {filteredSuggestions.map((tag) => (
              <div
                key={tag.value}
                className="tag-suggestion"
                onClick={() => selectSuggestion(tag)}
              >
                {tag.label}
              </div>
            ))}
          </div>
        )}

        {showSuggestions &&
          filteredSuggestions.length === 0 &&
          onCreateTag &&
          inputValue && (
            <div className="tags-suggestions">
              <div
                className="tag-suggestion create-new"
                onClick={() => addTag(inputValue)}
              >
                + Create &ldquo;{inputValue}&rdquo;
              </div>
            </div>
          )}
      </div>

      {helperText && <div className="tags-helper-text">{helperText}</div>}
    </div>
  );
};

export default TagsInput;
